import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Sparkles } from "lucide-react";
import axios from "axios";
import { API } from "@/lib/api";
import { PublicFooter } from "@/components/PublicShell";
import { StripeBadge } from "@/components/StripeBadge";
import { Button } from "@/components/ui/button";

const pub = axios.create({ baseURL: API });

export const BillingCycleToggle = ({ cycle, setCycle, discount = 20 }) => (
  <div className="inline-flex p-1 bg-secondary rounded-xl">
    {[["monthly", "Monthly"], ["yearly", `Annual — save ${discount}%`]].map(([id, label]) => (
      <button
        key={id}
        data-testid={`cycle-${id}`}
        onClick={() => setCycle(id)}
        className={`px-5 py-2 rounded-lg text-sm font-bold transition-colors ${
          cycle === id ? "bg-card shadow-sm text-primary" : "text-muted-foreground hover:text-foreground"}`}
      >
        {label}
      </button>
    ))}
  </div>
);

export const PlanCard = ({ plan, currency, cycle, side, cta, onCta, highlight, footer }) => {
  const yearly = cycle === "yearly";
  const amount = yearly ? plan.price_yearly : plan.price;
  return (
    <div
      data-testid={`pricing-card-${side}-${plan.id}`}
      className={`rounded-2xl p-7 border flex flex-col transition-all ${
        highlight ? "bg-card border-brand/50 shadow-lg shadow-brand/5" : "bg-card border-border"}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="font-display text-lg font-bold text-slate-900 dark:text-slate-100">{plan.name}</div>
        {highlight && (
          <span className="px-2 py-0.5 rounded-full bg-brand-soft text-brand text-[10px] font-bold uppercase tracking-wider border border-brand/25">
            Most capable
          </span>
        )}
      </div>
      <div className="mt-4 flex items-baseline gap-1.5">
        <span className="font-display text-4xl font-extrabold text-slate-900 dark:text-white tabular-nums">
          {amount === 0 ? "Free" : `${currency} ${amount}`}
        </span>
        {amount > 0 && <span className="text-sm text-muted-foreground">/ {yearly ? "year" : "month"}</span>}
      </div>
      {amount > 0 && yearly && (
        <div className="text-xs font-semibold text-brand mt-1">
          {currency} {Math.round((plan.price_yearly / 12) * 100) / 100} a month, billed annually
        </div>
      )}
      <ul className="mt-6 space-y-2.5 flex-1">
        {plan.perks.map((perk) => (
          <li key={perk} className="flex items-start gap-2.5 text-sm text-muted-foreground">
            <Check className="w-4 h-4 text-brand shrink-0 mt-0.5" /> {perk}
          </li>
        ))}
      </ul>
      {footer}
      <Button
        data-testid={`pricing-cta-${side}-${plan.id}`}
        variant={amount > 0 ? "default" : "outline"}
        onClick={onCta}
        className="w-full mt-6 h-11 font-semibold"
      >
        {cta}
      </Button>
    </div>
  );
};

export default function Pricing() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState(null);
  const [side, setSide] = useState("contractor");
  const [cycle, setCycle] = useState("monthly");

  useEffect(() => {
    pub.get("/billing/plans").then((r) => setPlans(r.data)).catch(() => {});
  }, []);

  const shown = side === "contractor" ? plans?.contractor_plans : plans?.vendor_plans;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/80 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between gap-4">
          <Link to="/" data-testid="pricing-brand" className="shrink-0">
            <img src="/matiqs-logo.png" alt="Matiqs" className="brand-logo h-8 w-auto" />
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            <Link to="/marketplace" className="px-3 py-1.5 rounded-lg text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors">Marketplace</Link>
            <Link to="/how-it-works" className="px-3 py-1.5 rounded-lg text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors">How it works</Link>
            <Link to="/faq" className="px-3 py-1.5 rounded-lg text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors">FAQ</Link>
          </nav>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" data-testid="pricing-signin" onClick={() => navigate("/login")} className="h-10 font-semibold">Sign in</Button>
            <Button data-testid="pricing-signup" onClick={() => navigate("/login?mode=register")} className="hidden sm:flex h-10 font-semibold">Get started</Button>
          </div>
        </div>
      </header>

      <section data-testid="pricing-page" className="max-w-6xl mx-auto px-5 py-16">
        <div className="text-center">
          <div className="text-[11px] uppercase tracking-[0.16em] text-brand font-bold">Pricing</div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 dark:text-white mt-3">
            Premium features for free.<br className="hidden sm:block" /> All features for less.
          </h1>
          <p data-testid="pricing-tagline" className="text-base md:text-lg font-semibold text-foreground mt-4">
            Powerful free plan. Unbeatable low plans.
          </p>
          <p className="text-base text-muted-foreground mt-3 max-w-2xl mx-auto">
            One account, two sides of the table. Contractors get a {plans?.trial_days || 30}-day free trial of
            Standard or Pro — no card needed to start.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8">
            <div className="inline-flex p-1 bg-secondary rounded-xl">
              {[["contractor", "For contractors"], ["vendor", "For vendors"]].map(([id, label]) => (
                <button
                  key={id}
                  data-testid={`pricing-tab-${id}`}
                  onClick={() => setSide(id)}
                  className={`px-5 py-2 rounded-lg text-sm font-bold transition-colors ${
                    side === id ? "bg-card shadow-sm text-primary" : "text-muted-foreground hover:text-foreground"}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <BillingCycleToggle cycle={cycle} setCycle={setCycle} discount={plans?.yearly_discount || 20} />
          </div>
        </div>

        <div data-testid="pricing-grid" className="grid gap-5 mt-12 md:grid-cols-3">
          {(shown || []).map((p) => (
            <PlanCard
              key={p.id}
              plan={p}
              side={side}
              cycle={cycle}
              currency={plans?.currency || "USD"}
              highlight={["pro", "gold"].includes(p.id)}
              cta={p.price === 0 ? "Start free" : `Get ${p.name}`}
              onCta={() => navigate(`/login?type=${side === "contractor" ? "buyer" : "vendor"}&mode=register`)}
              footer={side === "contractor" && p.price > 0 ? (
                <div className="text-xs font-semibold text-brand mt-4 inline-flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5" /> {plans?.trial_days || 30}-day free trial
                </div>
              ) : null}
            />
          ))}
        </div>

        <p className="text-xs text-muted-foreground text-center mt-8">
          All prices in {plans?.currency || "USD"}, cancel any time. Annual plans save {plans?.yearly_discount || 20}%.
          Vendor search and manual quoting are never capped.
        </p>

        <div className="flex justify-center mt-6">
          <StripeBadge testid="pricing-stripe-badge" />
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
